/**
 * CapabilityGrid — the workspace's own version of the M1 adapter matrix.
 * Thirteen tiles; each shows its live status or target milestone.
 */
import type { LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import type { CapabilityMatrix, CapabilityRow } from "@/entities/workspace";
import { CAPABILITY_META, CAPABILITY_ORDER, FALLBACK_ICON } from "@/entities/workspace";
import type { Workspace } from "@/entities/workspace";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

const CAPABILITY_ROUTE: Partial<Record<string, string>> = {
  brand_brain: "/brand-brain",
  campaigns: "/ai-studio",
  ai_memory: "/ai-studio",
};

interface Props {
  workspace: Workspace | null;
  matrix: CapabilityMatrix | undefined;
  isLoading: boolean;
}

export function CapabilityGrid({ workspace, matrix, isLoading }: Props): JSX.Element {
  const byKey: Record<string, CapabilityRow> = {};
  matrix?.capabilities.forEach((c) => {
    byKey[c.key] = c;
  });

  return (
    <Card
      data-testid="capability-grid"
      className="border-border/70 bg-card/70 shadow-sm backdrop-blur"
    >
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div className="space-y-1.5">
          <CardTitle className="font-display text-2xl font-semibold tracking-tight">
            {workspace ? workspace.profile.display_name : "Workspace"} · Capabilities
          </CardTitle>
          <CardDescription className="max-w-lg text-sm text-muted-foreground">
            Every workspace owns thirteen capability slots. Live tiles are
            usable today; empty tiles show the milestone that populates them.
          </CardDescription>
        </div>
        <span
          className="hidden rounded-full border border-border/70 bg-background/60 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground md:inline-flex"
        >
          docs/adr/0012
        </span>
      </CardHeader>
      <CardContent>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CAPABILITY_ORDER.map((key) => {
            const meta = CAPABILITY_META[key];
            const row = byKey[key];
            const Icon: LucideIcon = meta?.icon ?? FALLBACK_ICON;
            const route = CAPABILITY_ROUTE[key];
            const inner = (
              <div
                key={key}
                data-testid={`capability-tile-${key}`}
                className={`group flex flex-col gap-2 rounded-lg border border-border/60 bg-background/40 p-4 transition hover:border-border ${route ? "cursor-pointer hover:bg-secondary/40" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="grid h-8 w-8 place-items-center rounded-md"
                    style={{
                      backgroundColor: workspace
                        ? `${workspace.profile.primary_color}18`
                        : "hsl(var(--secondary))",
                      color: workspace?.profile.primary_color ?? "currentColor",
                    }}
                  >
                    <Icon className="h-4 w-4" />
                  </span>
                  {isLoading || !row ? (
                    <Skeleton className="h-5 w-16" />
                  ) : (
                    <StatusChip row={row} />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">{meta?.label ?? key}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {meta?.description}
                  </p>
                </div>
              </div>
            );
            return route ? (
              <Link key={key} to={route} className="contents">{inner}</Link>
            ) : inner;
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusChip({ row }: { row: CapabilityRow }): JSX.Element {
  if (row.status === "live") {
    return (
      <span
        data-testid={`capability-status-${row.key}`}
        className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-emerald-700 dark:text-emerald-400"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Live
        {typeof row.count === "number" ? ` · ${row.count}` : null}
      </span>
    );
  }
  return (
    <span
      data-testid={`capability-status-${row.key}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
    >
      Coming · {row.target_milestone}
    </span>
  );
}
